import { forwardRef, useRef, useImperativeHandle, useEffect, useState, Fragment } from 'react';
import type {
  ClipboardEvent,
  FunctionComponent,
  PropsWithoutRef,
  Ref,
  RefObject,
  Dispatch,
  SetStateAction,
  ComponentType
} from 'react';
import styled from 'styled-components';

import { defaultThemeProp } from '../../../theme';
import {
  useConfiguration,
  useFocusWithin,
  useI18n,
  useOuterEvent,
  useTestIds,
  useUID
} from '../../../hooks';
import type { ForwardProps } from '../../../types';
import FormField from '../../FormField';
import Flex from '../../Flex';
import Icon, { registerIcon } from '../../Icon';
import * as calendarIcon from '../../Icon/icons/calendar.icon';
import DatePicker from '../Picker/DatePicker';
import type DatePickerProps from '../Picker/DatePicker.types';
import { StyledFormControl } from '../../FormControl';
import type { FormControlProps } from '../../FormControl';
import type DateTimeProps from '../DateTime.types';
import type { DateTimeCallbackParameter } from '../DateTime.types';
import { createClassName, withTestIds } from '../../../utils';
import { getDateInputTestIds } from '../DateTime.test-ids';
import HiddenText from '../../HiddenText';

import {
  getDateFormat,
  getDateTimeParts,
  parsePastedTextToDate,
  parseToDate,
  validateDate,
  generatePickerButtonAriaLabel
} from './utils';
import StyledDateTime, {
  StyledDatePickerDialog,
  StyledDialogContent,
  StyledInputButton,
  StyledInputContainer
} from './DateTime.styles';
import useAutoFocusNextInput from './useAutoFocusNextInput';
import PartInput from './PartInput';
import usePickerButton from './usePickerButton';
import type { DateParts } from './DateTimeInput.types';

registerIcon(calendarIcon);

export interface DateInputHandleValue {
  setOpen: Dispatch<SetStateAction<boolean>>;
}

type AutoFillDate = AutoFillBase | `${OptionalPrefixToken<AutoFillSection>}bday`;

export interface DateInputProps extends DateTimeProps {
  /**
   * Display additional column with week number in date picker popover.
   * @default false
   */
  showWeekNumber?: boolean;
  /** Changes default date picker */
  picker?: {
    renderer: ComponentType;
    rendererProps?: Partial<DatePickerProps> & ForwardProps;
  };
  /** Pass a heading and content to show additional information on the field. */
  additionalInfo?: FormControlProps['additionalInfo'];
  /** Flag defining autocompletion behavior. */
  autoComplete?: AutoFillDate;
  /** Additional properties for imperative control. */
  handle?: Ref<DateInputHandleValue>;
}

export const StyledDateInput = styled(StyledDateTime)<DateInputProps>`
  padding-inline-end: 0;
`;

StyledDateInput.defaultProps = defaultThemeProp;

const convertToCallbackParameter = (
  date: Date,
  { year, month: monthIndex, day }: DateParts<string | undefined>
): DateTimeCallbackParameter => {
  if ([year, monthIndex, day].every(Boolean)) {
    if (!validateDate(Number(year), Number(monthIndex) + 1, Number(day))) {
      return {
        valueAsISOString: '',
        valueAsTimestamp: NaN,
        state: 'invalid'
      };
    }

    date.setUTCFullYear(Number(year), Number(monthIndex), Number(day));
    return {
      valueAsISOString: date.toISOString(),
      valueAsTimestamp: date.getTime()
    };
  }
  if ([year, monthIndex, day].every(p => !p)) {
    return {
      valueAsISOString: undefined,
      valueAsTimestamp: undefined
    };
  }
  return {
    valueAsISOString: '',
    valueAsTimestamp: NaN,
    state: 'incomplete'
  };
};

const DateInput: FunctionComponent<DateInputProps & ForwardProps> = forwardRef(function DateInput(
  props: PropsWithoutRef<DateInputProps>,
  ref: Ref<HTMLFieldSetElement>
) {
  const { locale } = useConfiguration();
  const t = useI18n();
  const {
    testId,
    value,
    min,
    max,
    id,
    label,
    labelHidden,
    info = getDateFormat(t, locale, 'date'),
    status,
    required,
    readOnly,
    disabled,
    onChange,
    onFocus,
    onBlur,
    onResolveSuggestion,
    showWeekNumber,
    timeZone,
    picker: { renderer: PickerComp = DatePicker, rendererProps = {} } = {},
    autoFocus,
    additionalInfo,
    handle,
    className,
    autoComplete = '_off',
    ...restProps
  } = props;

  const testIds = useTestIds(testId, getDateInputTestIds);
  const yearPartId = useUID();

  const containerRef = useRef<HTMLElement>(null);

  const date = value !== undefined && value !== '' ? parseToDate(value) : undefined;

  const [yearInputRef, monthInputRef, dayInputRef] = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  const [hasValue, setHasValue] = useState(!!date);
  useEffect(() => {
    setHasValue(
      !!(yearInputRef.current?.value || monthInputRef.current?.value || dayInputRef.current?.value)
    );
  }, [date]);

  const pickParts = (): DateParts<string | undefined> => {
    return {
      day: dayInputRef.current?.value || undefined,
      month: monthInputRef.current?.value
        ? (Number(monthInputRef.current.value) - 1).toString()
        : undefined,
      year: yearInputRef.current?.value || undefined
    };
  };

  const currentlySelectedDate = () => {
    const parts = pickParts();
    if (Object.values(parts).every(Boolean)) {
      const year = Number(parts.year);
      const monthIndex = Number(parts.month);
      const day = Number(parts.day);

      return validateDate(year, monthIndex + 1, day)
        ? new Date(Date.UTC(year, monthIndex, day))
        : undefined;
    }
  };

  const [open, setOpen, , setPopoverRef, datePickerButtonEl, setDatePickerButtonEl] =
    usePickerButton();

  const inputContainerRef = useRef<HTMLDivElement>(null);

  const onFocusChange = (focused: boolean) => {
    const callbackParam = convertToCallbackParameter(
      date ? new Date(date) : new Date(),
      pickParts()
    );
    if (onFocus && focused) onFocus(callbackParam);
    if (!focused) {
      if (inputContainerRef.current) inputContainerRef.current.scrollTop = 0;
      onBlur?.(callbackParam);
    }
  };

  const onInputChange = () => {
    onChange?.(convertToCallbackParameter(date ? new Date(date) : new Date(), pickParts()));
    datePickerButtonEl?.setAttribute(
      'aria-label',
      generatePickerButtonAriaLabel(currentlySelectedDate(), 'date', locale, t, open)
    );
    if (dayInputRef.current?.value || monthInputRef.current?.value || yearInputRef.current?.value) {
      setHasValue(true);
    } else {
      setHasValue(false);
    }
  };

  const setInputValue = (newValue?: Date) => {
    if (dayInputRef.current && monthInputRef.current && yearInputRef.current) {
      dayInputRef.current.value = newValue?.getUTCDate().toString().padStart(2, '0') ?? '';
      monthInputRef.current.value =
        (newValue && (newValue.getUTCMonth() + 1).toString().padStart(2, '0')) ?? '';
      yearInputRef.current.value = newValue?.getUTCFullYear().toString() ?? '';
      setHasValue(!!newValue);
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    const text = e.clipboardData.getData('text');
    const newDate = parsePastedTextToDate(text, locale, date || new Date(), 'date');
    if (newDate) {
      e.preventDefault();
      setInputValue(newDate);
      onInputChange();
    }
  };

  // autoclose picker on focusing out
  useOuterEvent('focusin', [containerRef], e => {
    if (containerRef.current?.contains(e.relatedTarget as HTMLElement)) setOpen(false);
  });

  const displayNames = new Intl.DisplayNames(locale, { style: 'long', type: 'dateTimeField' });

  const minYear = min !== undefined ? parseToDate(min).getUTCFullYear() : 1;
  const maxYear = max !== undefined ? parseToDate(max).getUTCFullYear() : 9999;

  const DayInput = (
    <PartInput
      data-testid={testIds.controlDay}
      key='day'
      placeholder={t('day_placeholder')}
      aria-label={displayNames.of('day')}
      value={date?.getUTCDate()}
      onChange={onInputChange}
      size={2}
      maxLength={2}
      readOnly={readOnly}
      min={1}
      max={31}
      autoComplete={
        (autoComplete && autoComplete.endsWith('bday')
          ? autoComplete.replace('bday', 'bday-day')
          : autoComplete) as AutoFill
      }
      ref={dayInputRef}
    />
  );

  const MonthInput = (
    <PartInput
      data-testid={testIds.controlMonth}
      key='month'
      placeholder={t('month_placeholder')}
      aria-label={displayNames.of('month')}
      value={date ? date.getUTCMonth() + 1 : undefined}
      onChange={onInputChange}
      size={2}
      maxLength={2}
      readOnly={readOnly}
      min={1}
      max={12}
      autoComplete={
        (autoComplete && autoComplete.endsWith('bday')
          ? autoComplete.replace('bday', 'bday-month')
          : autoComplete) as AutoFill
      }
      ref={monthInputRef}
    />
  );

  const YearInput = (
    <Fragment key='year'>
      <PartInput
        data-testid={testIds.controlYear}
        placeholder={t('year_placeholder')}
        aria-label={displayNames.of('year')}
        aria-describedby={yearPartId}
        value={date?.getUTCFullYear()}
        onChange={onInputChange}
        size={4}
        maxLength={4}
        padWithZeros={false}
        readOnly={readOnly}
        min={minYear}
        max={maxYear}
        autoComplete={
          (autoComplete && autoComplete.endsWith('bday')
            ? autoComplete.replace('bday', 'bday-year')
            : autoComplete) as AutoFill
        }
        ref={yearInputRef}
      />
      <HiddenText id={yearPartId}>{t('year_input_description')}</HiddenText>
    </Fragment>
  );

  useFocusWithin([containerRef], onFocusChange);

  useImperativeHandle(handle, () => ({ setOpen }));

  const PickerButton = (
    <>
      <StyledInputButton
        data-testid={testIds.openClosePicker}
        variant='simple'
        icon
        ref={setDatePickerButtonEl}
        onClick={() => setOpen(cur => !cur)}
        aria-label={generatePickerButtonAriaLabel(currentlySelectedDate(), 'date', locale, t, open)}
        readOnly={readOnly}
        disabled={disabled}
      >
        <Icon name='calendar' />
      </StyledInputButton>
      {open && datePickerButtonEl && (
        <StyledDatePickerDialog
          ref={setPopoverRef}
          target={datePickerButtonEl}
          placement='bottom-end'
          aria-label={t('choose_date')}
        >
          <Flex container={{ direction: 'column' }} as={StyledDialogContent}>
            <PickerComp
              testId={testIds.picker}
              selected={currentlySelectedDate()}
              min={min}
              max={max}
              showWeekNumber={showWeekNumber}
              timeZone={timeZone}
              {...rendererProps}
              onChange={selected => {
                setInputValue(selected);
                setOpen(false);
                setTimeout(() => datePickerButtonEl?.focus());
                onInputChange();
                rendererProps?.onChange?.(selected);
              }}
            />
          </Flex>
        </StyledDatePickerDialog>
      )}
    </>
  );

  const orderedRefs: RefObject<HTMLInputElement>[] = [];

  const Comp = (
    <Flex
      data-testid={label ? undefined : testIds.root}
      as={StyledDateInput}
      forwardedAs={StyledFormControl}
      hasSuggestion={status === 'pending' && !!onResolveSuggestion}
      container={{ alignItems: 'center', wrap: 'nowrap', justify: 'between' }}
      status={status}
      readOnly={readOnly}
      disabled={disabled}
      ref={containerRef}
      onClick={(e: MouseEvent) => {
        if (e.target === containerRef.current) orderedRefs[0].current?.focus();
      }}
      {...restProps}
      className={createClassName('date-input', className)}
    >
      <Flex
        ref={inputContainerRef}
        as={StyledInputContainer}
        container={{ alignItems: 'center', wrap: 'wrap' }}
        onPaste={onPaste}
      >
        {getDateTimeParts(locale).map(part => {
          switch (part) {
            case 'day':
              orderedRefs.push(dayInputRef);
              return DayInput;
            case 'month':
              orderedRefs.push(monthInputRef);
              return MonthInput;
            case 'year':
              orderedRefs.push(yearInputRef);
              return YearInput;
            default:
              return part;
          }
        })}
      </Flex>
      {!readOnly && (
        <Flex container>
          {hasValue && (
            <StyledInputButton
              data-testid={testIds.clearCurrentDate}
              aria-label={t('clear_current_date')}
              icon
              onClick={() => {
                setInputValue(undefined);
                setTimeout(() => datePickerButtonEl?.focus());
                onInputChange();
              }}
              variant='simple'
              compact
              label={t('clear')}
            >
              <Icon name='times' />
            </StyledInputButton>
          )}
          {PickerButton}
        </Flex>
      )}
    </Flex>
  );

  useEffect(() => {
    if (autoFocus) orderedRefs[0].current?.focus();
  }, [autoFocus]);

  const { description, descriptionID } = useAutoFocusNextInput(orderedRefs);

  return label ? (
    <>
      <FormField
        testId={testIds}
        as='fieldset'
        aria-labelledby={id}
        label={label}
        labelHidden={labelHidden}
        id={id}
        info={info}
        readOnly={readOnly}
        status={status}
        required={required}
        disabled={disabled}
        onResolveSuggestion={onResolveSuggestion}
        additionalInfo={additionalInfo}
        aria-describedby={descriptionID}
        ref={ref}
      >
        {Comp}
      </FormField>
      {description}
    </>
  ) : (
    Comp
  );
});

export default withTestIds(DateInput, getDateInputTestIds);
